import { useIsMarkdownNativeEditorEnabled } from '@/features/flags';
import {
  type TemplateEditorTabType,
  type TemplateEditorTabViewProps,
} from '@heidi/growth';
import { useDebouncedCallback } from 'use-debounce';
import { TemplateEditorTabLegacy } from './template-editor-tab-legacy';
import { TemplateEditorTabMarkdownNative } from './template-editor-tab-markdown-native';

export { DEFAULT_EXAMPLE_CONTENT, DEFAULT_STRUCTURE_CONTENT } from '@heidi/growth';

interface TemplateEditorTabProps {
  content: string;
  onContentChange?: (content: string) => void;
  tabType: TemplateEditorTabType;
  placeholder?: string;
  isEditable?: boolean;
  onSaved?: () => void;
}

/**
 * Routes the onboarding template editor to the markdown-native kit or the
 * legacy vMarkdown one. A component boundary, not a branch inside one hook:
 * only the routed kit may build a TipTap instance.
 *
 * The write-back debounce lives here so both kits share one timing and one
 * `onSaved` contract.
 */
export function TemplateEditorTab({
  content,
  onContentChange,
  tabType,
  placeholder,
  isEditable = false,
  onSaved,
}: TemplateEditorTabProps) {
  const isMarkdownNative = useIsMarkdownNativeEditorEnabled();

  const debouncedOnContentChange = useDebouncedCallback((newContent: string) => {
    onContentChange?.(newContent);
    onSaved?.();
  }, 300);

  const viewProps: TemplateEditorTabViewProps = {
    content,
    // Absent means "no write-back wanted" — the editors skip their update
    // handler entirely rather than debouncing into a no-op.
    onContentChange: onContentChange ? debouncedOnContentChange : undefined,
    tabType,
    placeholder,
    isEditable,
  };

  return isMarkdownNative ? (
    <TemplateEditorTabMarkdownNative {...viewProps} />
  ) : (
    <TemplateEditorTabLegacy {...viewProps} />
  );
}
